// =============================================================================
// Pintravel — Design Tokens
// =============================================================================
// Raw values from the Claude Design handoff. This file is the single source of
// truth for brand colour, radius and shadow values; `theme.ts` maps them onto
// MUI's palette, and screens read the brand-specific ones (navy, tints, amber,
// category colours) via `theme.palette.brand.*`.
//
// Two named schemes:
//   light — "koral / zachód słońca": warm cream base, coral accent
//   dark  — "nocny lot": navy base, same coral accent
//
// Theme-switching rule: the coral primary and the logo mark never change. Only
// the neutral base flips (cream ↔ navy).
// =============================================================================

// -----------------------------------------------------------------------------
// Brand constants — identical in both schemes
// -----------------------------------------------------------------------------

/** Coral. CTAs, accents, active tabs, FAB, logo mark. */
export const CORAL = '#E85D3F';
/** Darker coral steps, used for the onboarding and profile gradients. */
export const CORAL_DARK = '#D14A2E';
export const CORAL_DEEPEST = '#B93A22';
/** Lighter coral for text/links on dark backgrounds, where #E85D3F lacks contrast. */
export const CORAL_ON_DARK = '#F08A6E';
/** Deep navy. Hero cards, dark secondary buttons, own chat bubble (light), expense header. */
export const NAVY = '#1E2A4A';

// -----------------------------------------------------------------------------
// Light scheme — "koral / zachód słońca"
// -----------------------------------------------------------------------------

export const lightTokens = {
  primary: CORAL,
  primaryDark: CORAL_DARK,
  primaryDeepest: CORAL_DEEPEST,
  /** Chip/icon backgrounds on primary, secondary button background. */
  primaryTint: '#FBEFE9',
  navy: NAVY,
  bg: '#FAF6F0',
  surface: '#FFFFFF',
  border: '#EEE8DC',
  text: '#241E17',
  textMuted: '#8A8072',
  /** Inactive tab-bar icons and other de-emphasised glyphs. */
  iconMuted: '#B9AE9D',
  /** Positive accent: online status, "you are owed" amounts. */
  teal: '#0E8C86',
  tealBright: '#14B8A6',
  /** Votes / pending states. */
  amber: '#E8A63F',
  amberBg: '#FDF3E3',
  amberText: '#B07A18',
  blue: '#2465D9',
  pink: '#D9486B',
  /** Positive amount rendered on the navy expense header. */
  success: '#6BE0A0',
  /** Composer / inset input fill. */
  inputFill: '#F4EFE7',
  /** Timeline connector line in the day plan. */
  timeline: '#EBD9CF',
  /** Row separator inside settings cards. */
  separator: '#F4EFE7',
} as const;

// -----------------------------------------------------------------------------
// Dark scheme — "nocny lot"
// -----------------------------------------------------------------------------

export const darkTokens = {
  primary: CORAL,
  primaryDark: CORAL_DARK,
  primaryDeepest: CORAL_DEEPEST,
  /** rgba tint keeps the coral readable as a button fill on navy. */
  primaryTint: 'rgba(232, 93, 63, 0.15)',
  navy: NAVY,
  bg: '#141C33',
  surface: NAVY,
  /** Tab bar and bar headers sit deeper than cards. */
  surfaceDeep: '#101830',
  border: '#2C3A61',
  text: '#FFFFFF',
  textMuted: '#8B93AD',
  iconMuted: '#556086',
  teal: '#14B8A6',
  tealBright: '#14B8A6',
  amber: '#E8A63F',
  amberBg: 'rgba(232, 166, 63, 0.14)',
  amberText: '#E8A63F',
  blue: '#2465D9',
  pink: '#D9486B',
  success: '#6BE0A0',
  inputFill: '#101830',
  timeline: '#2C3A61',
  separator: '#2C3A61',
} as const;

// Declared explicitly rather than derived from one of the objects above: `as
// const` would pin every field to its literal value, so the two schemes would
// not be assignable to a shared type.
export interface BrandTokens {
  primary: string;
  primaryDark: string;
  primaryDeepest: string;
  primaryTint: string;
  navy: string;
  bg: string;
  surface: string;
  /** Bars sit deeper than cards in dark mode; in light mode they are just white. */
  surfaceDeep: string;
  border: string;
  text: string;
  textMuted: string;
  iconMuted: string;
  teal: string;
  tealBright: string;
  amber: string;
  amberBg: string;
  amberText: string;
  blue: string;
  pink: string;
  success: string;
  inputFill: string;
  timeline: string;
  separator: string;
}

export const lightBrand: BrandTokens = {
  ...lightTokens,
  surfaceDeep: '#FFFFFF',
};

export const darkBrand: BrandTokens = darkTokens;

// -----------------------------------------------------------------------------
// Radii
// -----------------------------------------------------------------------------

export const radii = {
  /** Hero card, bottom sheet top corners. */
  hero: 24,
  /** Standard card. */
  card: 18,
  /** Denser card (list rows, timeline items). */
  cardTight: 16,
  button: 16,
  /** Icon tiles (category squares). */
  tile: 12,
  /** Inputs and pills as tall as a button. */
  pill: 16,
  /**
   * Chips and chip-like labels. A 16px radius on a 30px chip turns it into a
   * full pill, unlike every button and field; 10 keeps the same corner, and
   * 8 on a small (24px) one.
   */
  chip: 10,
  chipSmall: 8,
} as const;

// -----------------------------------------------------------------------------
// Shadows
// -----------------------------------------------------------------------------
// The design is deliberately near-flat: cards carry a 1px border and no shadow.
// Only these three elements float.

export const brandShadows = {
  fab: '0 8px 20px rgba(232, 93, 63, 0.4)',
  floating: '0 4px 14px rgba(35, 28, 18, 0.1)',
  sheet: '0 -8px 30px rgba(35, 28, 18, 0.12)',
} as const;

// -----------------------------------------------------------------------------
// Gradients
// -----------------------------------------------------------------------------

/** Onboarding / login full-screen background. */
export const onboardingGradient = `linear-gradient(175deg, ${CORAL} 0%, ${CORAL_DARK} 55%, ${CORAL_DEEPEST} 100%)`;
/** Profile header. */
export const profileGradient = `linear-gradient(160deg, ${CORAL} 0%, ${CORAL_DARK} 100%)`;

/**
 * Placeholder gradients for images we don't have yet (trip covers, place
 * thumbnails, post photos). Picked deterministically by hashing an id so the
 * same entity always renders the same colours.
 */
export const placeholderGradients = [
  `linear-gradient(135deg, ${CORAL} 0%, ${CORAL_DEEPEST} 100%)`,
  `linear-gradient(135deg, #2465D9 0%, ${NAVY} 100%)`,
  `linear-gradient(135deg, #14B8A6 0%, #0E8C86 100%)`,
  `linear-gradient(135deg, #D9486B 0%, #B93A22 100%)`,
  `linear-gradient(135deg, #E8A63F 0%, ${CORAL_DARK} 100%)`,
  `linear-gradient(135deg, ${NAVY} 0%, #101830 100%)`,
] as const;

/** Stable gradient pick for a given entity id. */
export function gradientFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % placeholderGradients.length;
  return placeholderGradients[index] ?? placeholderGradients[0];
}

// -----------------------------------------------------------------------------
// Layout
// -----------------------------------------------------------------------------

export const layout = {
  /**
   * Horizontal screen padding. 16, and the feed's post card lines its avatar,
   * buttons and caption up with it, so the whole app shares one edge.
   */
  screenX: 16,
  /** Gap between sections. */
  sectionGap: 18,
  /** Gap between cards in a list. */
  cardGap: 11,
} as const;
